import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import Signup from './components/Signup';
import DemoLogin from './components/DemoLogin';
import ModernDashboard from './components/ModernDashboard';
import NMICheckout from './components/NMICheckout';
import PaymentSuccess from './components/PaymentSuccess';
import PricingPage from './components/PricingPage';
import SubscriptionManagement from './components/SubscriptionManagement';
import SubscriptionSuccess from './components/SubscriptionSuccess';
import SubscriptionCancel from './components/SubscriptionCancel';
import GlobalStyles from './styles/GlobalStyles';

export interface User {
  id: string;
  email: string;
}

// Protected route wrapper component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        fontSize: '18px',
        color: '#6b7280'
      }}>
        Loading...
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }

  return <>{children}</>;
};

function AppContent() {
  const { user, logout } = useAuth();

  return (
    <>
      <GlobalStyles />
      <div className="App">
        <Routes>
          {/* Public routes - accessible without login */}
          <Route path="/demo" element={<DemoLogin />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/checkout" element={<NMICheckout />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />

          {/* Stripe subscription routes - accessible without login */}
          <Route path="/subscription/success" element={<SubscriptionSuccess />} />
          <Route path="/subscription/cancel" element={<SubscriptionCancel />} />

          {/* Protected routes - require login */}
          <Route
            path="/pricing"
            element={
              <ProtectedRoute>
                <PricingPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/subscription"
            element={
              <ProtectedRoute>
                <SubscriptionManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <ModernDashboard
                  user={user ? { id: user.orgId, email: user.email } : { id: '', email: '' }}
                  onLogout={logout}
                />
              </ProtectedRoute>
            }
          />

          {/* Default route - dashboard */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <ModernDashboard
                  user={user ? { id: user.orgId, email: user.email } : { id: '', email: '' }}
                  onLogout={logout}
                />
              </ProtectedRoute>
            }
          />

          {/* Redirect any unknown routes to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
